/**
 * Constants, design tokens, and config for Project Asset Tree View.
 * Keeps layout, styling, and hierarchy rules in one place.
 */

// --- Design System Tokens ---
export const DESIGN_TOKENS = {
  colors: {
    primary: {
      50: '#EFF6FF',
      100: '#DBEAFE',
      200: '#BFDBFE',
      300: '#93C5FD',
      400: '#60A5FA',
      500: '#3B82F6',
      600: '#2563EB',
      700: '#1D4ED8',
      800: '#1E40AF',
      900: '#1E3A8A',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    success: {
      50: '#ECFDF5',
      100: '#D1FAE5',
      500: '#10B981',
      600: '#059669',
      gradient: 'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)'
    },
    accent: {
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      purple: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      teal: 'linear-gradient(135deg, #0ea5e9 0%, #22d3ee 100%)'
    },
    glass: {
      white: 'rgba(255, 255, 255, 0.7)',
      border: 'rgba(255, 255, 255, 0.18)'
    }
  },
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    glow: '0 0 20px rgba(99, 102, 241, 0.3)',
    glowGreen: '0 0 20px rgba(16, 185, 129, 0.3)',
    glowBlue: '0 0 20px rgba(59, 130, 246, 0.3)'
  },
  animations: {
    spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
    smooth: 'cubic-bezier(0.4, 0, 0.2, 1)',
    bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    easeOut: 'cubic-bezier(0.16, 1, 0.3, 1)'
  },
  spacing: {
    horizontal: { base: 24, depthMultiplier: 1.04, minGap: 16, maxGap: 40 },
    vertical: { base: 36, depthMultiplier: 0.92, minGap: 16, maxGap: 48 }
  }
};

// --- Animation variants (framer-motion) ---
export const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

export const scaleIn = {
  initial: { opacity: 0, scale: 0.9 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.9 }
};

export const slideInRight = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 }
};

// --- Hierarchy constraints (solar plant rules) ---
export const HIERARCHY_CONSTRAINTS = {
  STRING: {
    MIN_MODULES: 10,
    MAX_MODULES: 30,
    DESCRIPTION: 'Each String must contain between 10 and 30 PV Modules'
  },
  CONVERSION: {
    MIN_STRINGS: 2,
    MAX_STRINGS: 3,
    DESCRIPTION: 'Each Conversion asset (Inverter) must have between 2 and 3 Strings'
  }
};

// --- Equipment card styling (site network — clean, high-contrast) ---
/** Card surface — near-white for crisp contrast on engineering grid */
export const EQUIPMENT_CARD_BG = '#FFFFFF';
/** Card border — slate-200 equivalent, readable on light canvas */
export const EQUIPMENT_CARD_BORDER = '#E2E8F0';
/** Selected / emphasis ring (used with inline styles where needed) */
export const EQUIPMENT_CARD_RING_FOCUS = 'rgba(37, 99, 235, 0.25)';

// --- Layout constants (tree dimensions) - no overlap, 20px space between all equipment ---
/** Minimum gap (px) between any two nodes - 20px on all sides */
export const MIN_NODE_GAP = 20;

export const LAYOUT = {
  /** Default card width — wide enough for asset type labels (e.g. STRING INVERTER) without clipping */
  NODE_WIDTH: 168,
  /** Min width for parent nodes so equipment name + "X children" display fully */
  PARENT_NODE_MIN_WIDTH: 220,
  NODE_HEIGHT: 52,
  MODULE_WIDTH: 18,
  MODULE_HEIGHT: 20,
  /** Horizontal space between sibling nodes / modules */
  HORIZONTAL_SPACING: 20,
  /** Gap between module cells (visible as light line - like solar panel grid) */
  MODULE_SPACING: 4,
  /** Padding inside the module grid frame (space between frame and first/last cell) */
  MODULE_GRID_PADDING: 6,
  /** Background for module grid area (shows as gap between cells and inside frame) */
  MODULE_GRID_GAP_BG: '#E5E7EB',
  /** Vertical space between wrapped sibling rows under the same parent */
  VERTICAL_SPACING: 24,
  /** Minimum gap between parent bottom and first child row (prevents child overlapping parent) */
  PARENT_CHILD_GAP: 32,
  /** Extra gap between root-level subtrees */
  ROOT_SPACING_MULTIPLIER: 2.5
} as const;

// --- Grid for background (layout is dynamic, no drag-move) ---
export const TREE_GRID = {
  /** Dot pitch for engineering-style canvas background */
  STEP: 20,
  /** Padding from tree content edge (20px all sides) */
  PADDING: 20
} as const;

// --- Zoom (used by useTreeZoom) ---
export const ZOOM_CONFIG = {
  min: 0.5,
  max: 2.0,
  step: 0.1,
  default: 1.0
} as const;

// --- Minimap ---
export const MINIMAP_CONFIG = {
  scale: 0.05,
  maxWidth: 200,
  maxHeight: 150,
  minWidth: 100,
  minHeight: 75
} as const;

/**
 * Inline "Solar DC Layout" summary panel — design tokens (used only when the layout hits the
 * virtual-node cap). Normal sites use per-string virtual rows instead; tasks still use one
 * equipment id plus `stringIndex` on the backend.
 */
export const DC_LAYOUT_PANEL = {
  /** Label column with "String N · M modules" on the left of each row */
  STRING_LABEL_WIDTH: 112,
  /** Width of each module cell in the panel grid */
  MODULE_CELL_WIDTH: 14,
  /** Height of each module cell in the panel grid */
  MODULE_CELL_HEIGHT: 16,
  /** Gap between module cells (horizontal + vertical within a row) */
  MODULE_CELL_GAP: 3,
  /** Vertical gap between two string rows inside the panel */
  STRING_ROW_GAP: 6,
  /** Padding inside the module-grid box of each string row */
  ROW_PADDING: 5,
  /** Top/bottom padding inside the panel body (below header, above footer) */
  PANEL_BODY_PADDING: 10,
  /** Space reserved for the panel header inside the card */
  HEADER_HEIGHT: 54,
  /** Space reserved for the panel footer (checkpoint / delete buttons) inside the card */
  FOOTER_HEIGHT: 44,
  /** Absolute max width of a single panel card (wraps modules to next line above this) */
  MAX_PANEL_WIDTH: 1040,
  /** Min width of the panel card so header + summary fit cleanly */
  MIN_PANEL_WIDTH: 320,
  /** Skip drawing individual cells above this per row — show a summary bar instead */
  MAX_CELLS_PER_ROW_RENDER: 120,
  /** Above this many string rows, collapse the grid into a summary density bar (perf + legibility) */
  MAX_STRING_ROWS_RENDER: 48,
  /**
   * Max module tiles per horizontal row inside one string’s framed “PV strip” before wrapping
   * to the next row (matches layout height in `treeLayout.getDcLayoutPanelDimensions`).
   */
  STRING_BOX_MAX_CELL_COLS: 14,
  /** Vertical chrome for the aluminum-style frame (`p-[3px]` ×2) around each string strip */
  STRING_BOX_FRAME_VERTICAL_EXTRA: 6,
  /** Reserved under the cell grid when `+N modules` overflow copy is shown */
  STRING_BOX_OVERFLOW_LINE_HEIGHT: 14
} as const;

/**
 * How many module tiles to draw and how they wrap inside one string’s framed PV strip.
 * Must stay aligned with `treeLayout.getDcLayoutPanelDimensions` for expanded DC layout cards.
 */
export function getDcLayoutStringBoxGrid(moduleCount: number): {
  cellsToDraw: number;
  cols: number;
  rows: number;
} {
  const cap = DC_LAYOUT_PANEL.MAX_CELLS_PER_ROW_RENDER;
  const maxCols = DC_LAYOUT_PANEL.STRING_BOX_MAX_CELL_COLS;
  const cellsToDraw = Math.min(Math.max(0, moduleCount), cap);
  if (cellsToDraw === 0) return { cellsToDraw: 0, cols: 1, rows: 1 };
  const cols = Math.min(maxCols, cellsToDraw);
  const rows = Math.ceil(cellsToDraw / cols);
  return { cellsToDraw, cols, rows };
}

/** Large-tree performance: layout, expand defaults, grid caps */
export const TREE_PERF = {
  /** Skip full O(n²) overlap resolution when there are more laid-out nodes than this */
  OVERLAP_RESOLVE_MAX_NODES: 320,
  /** New nodes auto-expand only if they have at most this many children (large branches start collapsed) */
  AUTO_EXPAND_MAX_CHILDREN: 96,
  /** Cap string module grid for layout + paint (remaining count shown in UI) */
  MAX_MODULE_GRID_CELLS: 480,
  /** Above this many layout nodes, skip framer-motion spring on standard cards */
  LIGHT_MOTION_NODE_THRESHOLD: 200,
  /** Max virtual module boxes per string from `pvModuleCount`; above → one summary row (perf) */
  STRING_PV_MODULE_BOXES_MAX: 500,
  /** Tree minimap: max dots rendered; larger trees are sampled for smooth pan/zoom */
  MINIMAP_MAX_NODE_DOTS: 450,
  /** Chunk size when loading equipment list (merges pages client-side; full list still in `data`) */
  EQUIPMENT_TREE_PAGE_SIZE: 2500
} as const;
